// Gel du plancher d'un trailing drawdown : OÙ et QUAND il cesse de monter.
//
// POURQUOI. Chaque prop firm fige son trailing à sa façon, et la règle vivait
// en dur dans le moteur : « +100 $ si le slug contient apex, sinon +0, en funded,
// et seulement quand le trailing atteint ce niveau ». Cas réel qui l'a cassée
// (LucidFlex 50k, 2026-10-16) : Lucid fige le plancher à initial + 100 $ DÈS LE
// PREMIER PAYOUT. Le trader, à 50 857 $ après un payout de 858 $, a perdu 767 $
// et sauté à 50 091 $ ; Edgely affichait un plancher à 48 091 $ et 2 000 $ de
// marge.
//
// La règle se règle donc par TYPE DE COMPTE dans l'admin (trois colonnes de
// `PropFirmAccountType`), lue en VIVANT et non depuis le gel des règles du
// compte (`ruleSnapshot`) : c'est une mécanique de la firm, pas un montant du
// palier, et une correction doit atteindre les comptes existants. Relever un
// plancher ne peut pas désactiver un compte : `breachIsPlausible` exige une
// perte d'au moins un drawdown complet sous l'initial.
//
// Colonnes nulles = comportement historique, au bit près.
//
// Module PUR : aucun import Wasp/Prisma.

import { payoutBalanceAmount, type PayoutAmounts } from "./payoutAmounts";
import { isPropFirmAdjustment } from "./walletPayoutRouting";

/**
 * Quand le plancher se fige à `initial + offset`.
 * - `trailing` : quand le trailing atteint ce niveau (Apex, Topstep combine…).
 * - `trailing_or_payout` : idem, OU dès la première sortie d'argent du compte
 *   (LucidFlex : « drawdown bloqué à 50 100 depuis ton premier payout »).
 * - `never` : le trailing suit le pic sans jamais s'arrêter.
 */
export const DRAWDOWN_LOCK_MODES = ["trailing", "trailing_or_payout", "never"] as const;
export type DrawdownLockMode = (typeof DRAWDOWN_LOCK_MODES)[number];

/** Phases où le gel s'applique : funded seulement, ou aussi en challenge. */
export const DRAWDOWN_LOCK_SCOPES = ["funded", "all"] as const;
export type DrawdownLockScope = (typeof DRAWDOWN_LOCK_SCOPES)[number];

/** Les trois colonnes de `PropFirmAccountType`, telles que lues en base. */
export interface DrawdownLockSettings {
  drawdownLockOffset?: number | null;
  drawdownLockMode?: string | null;
  drawdownLockScope?: string | null;
}

export interface DrawdownLockRule {
  /** Le plancher se fige à `initialBalance + offset`. */
  offset: number;
  mode: DrawdownLockMode;
  scope: DrawdownLockScope;
}

/**
 * Décalage historique, avant les colonnes : +100 $ chez Apex, 0 ailleurs.
 * `includes` et non `startsWith` : les templates prod s'appellent
 * « custom-apextraderfunding-<id> », pas « apex ».
 */
export function legacyLockOffset(templateSlug: string | null | undefined): number {
  return templateSlug?.toLowerCase().includes("apex") ? 100 : 0;
}

const isMode = (v: unknown): v is DrawdownLockMode =>
  typeof v === "string" && (DRAWDOWN_LOCK_MODES as readonly string[]).includes(v);
const isScope = (v: unknown): v is DrawdownLockScope =>
  typeof v === "string" && (DRAWDOWN_LOCK_SCOPES as readonly string[]).includes(v);

/**
 * Règle de gel effective. Chaque colonne nulle (ou illisible) retombe sur le
 * comportement historique, indépendamment des deux autres.
 */
export function resolveDrawdownLockRule(
  settings: DrawdownLockSettings | null | undefined,
  templateSlug: string | null | undefined,
): DrawdownLockRule {
  const offset = settings?.drawdownLockOffset;
  return {
    offset: offset != null && Number.isFinite(offset) ? offset : legacyLockOffset(templateSlug),
    mode: isMode(settings?.drawdownLockMode) ? settings!.drawdownLockMode as DrawdownLockMode : "trailing",
    scope: isScope(settings?.drawdownLockScope) ? settings!.drawdownLockScope as DrawdownLockScope : "funded",
  };
}

/**
 * Plancher effectif d'un trailing, gel appliqué.
 *
 * Un plancher figé est un `min` : il ne peut que BLOQUER la montée du trailing,
 * jamais le faire descendre. Seul le gel au payout le pose directement au
 * niveau de gel, puisque la firm le fige à cet instant quel que soit le pic.
 */
export function applyDrawdownLock(input: {
  trailingFloor: number;
  initialBalance: number;
  rule: DrawdownLockRule;
  funded: boolean;
  /** Au moins une sortie d'argent du compte (payout, transfert wallet). */
  hasWithdrawal: boolean;
}): number {
  const { trailingFloor, initialBalance, rule, funded, hasWithdrawal } = input;
  if (rule.mode === "never") return trailingFloor;
  if (rule.scope === "funded" && !funded) return trailingFloor;

  const lockLevel = initialBalance + rule.offset;
  if (rule.mode === "trailing_or_payout" && funded && hasWithdrawal) return lockLevel;
  return Math.min(trailingFloor, lockLevel);
}

/**
 * Le compte a-t-il déjà vu sortir de l'argent ?
 *
 * Même définition que la déduction du solde (`payoutBalanceAmount`) : un
 * retrait du wallet TPT ne sort pas du compte (l'argent l'avait quitté au
 * transfert), il ne compte donc pas. Les sorties d'avant un reset non plus :
 * le compte est reparti à neuf. Une reprise de PnL par la firm (infraction
 * MAE Apex) non plus : l'argent sort, mais ce n'est pas un payout.
 */
export function hasWithdrawnFromAccount(
  payouts: readonly (PayoutAmounts & { date?: Date | string | null })[],
  resetAt?: Date | string | null,
): boolean {
  const resetMs = resetAt ? new Date(resetAt).getTime() : null;
  return payouts.some((p) => {
    if (payoutBalanceAmount(p) <= 0 || isPropFirmAdjustment(p)) return false;
    if (resetMs === null || !p.date) return true;
    return new Date(p.date).getTime() >= resetMs;
  });
}
